import React from 'react';
import styled from 'styled-components';
import { Flex } from '../components/Layout';
import { spacing } from '../tokens/spacing';
import { borders } from '../tokens/borders';
import { typography } from '../tokens/typography';
import { breakpoints } from '../tokens/breakpoints';
import { headerWatermarkStyles } from '../../components/dashboard/dashboardWatermark';
import { MonumentCaption } from '../../components/dashboard/MonumentCaption';
import { useMonumentWatermark } from '../../hooks/useMonumentWatermark';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  breadcrumbs?: React.ReactNode;
  /** Kept for API compatibility; watermark is always applied. */
  pattern?: boolean;
  /** Kept for API compatibility; ignored in favor of the museum watermark treatment. */
  gradient?: boolean;
  /** Optional compact meta row under the subtitle (badge, links, etc.). */
  meta?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

const HeaderContainer = styled.header`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  min-height: 180px;
  display: flex;
  align-items: center;
  padding: ${spacing[8]};
  margin-bottom: ${spacing[6]};
  border-radius: ${borders.radius.lg};

  ${headerWatermarkStyles}

  @media (max-width: ${breakpoints.md}) {
    padding: ${spacing[5]} ${spacing[4]};
    min-height: auto;
  }
`;

const HeaderContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
`;

const AccentBar = styled.span`
  display: block;
  width: 58px;
  height: 3px;
  border-radius: 2px;
  background: ${props => props.theme.colors.accent};
  margin-bottom: ${spacing[4]};
`;

const TitleRow = styled(Flex)`
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[4]};
  width: 100%;
`;

const TextBlock = styled.div`
  min-width: 0;
  flex: 1;
  max-width: 40rem;
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${typography.fontFamily.display};
  font-size: clamp(1.75rem, 2.5vw, 2.25rem);
  font-weight: 600;
  line-height: 1.2;
  color: ${props => props.theme.colors.text};
`;

const Subtitle = styled.p`
  margin: ${spacing[4]} 0 0 0;
  max-width: 36rem;
  line-height: ${typography.lineHeight.relaxed};
  font-size: ${typography.fontSize.base};
  font-family: ${typography.fontFamily.primary};
  color: ${props => props.theme.colors.textSecondary};
`;

const ActionsRow = styled.div`
  display: flex;
  gap: ${spacing[3]};
  align-items: center;
  flex-shrink: 0;
  flex-wrap: wrap;

  @media (max-width: ${breakpoints.md}) {
    width: 100%;
  }
`;

const BreadcrumbsWrap = styled.div`
  margin-bottom: ${spacing[4]};
`;

/**
 * Shared page hero — same museum watermark + typography treatment as the member dashboard header.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  actions,
  breadcrumbs,
  meta,
  children,
  className,
}) => {
  const watermark = useMonumentWatermark();

  return (
    <HeaderContainer className={className} style={watermark.style}>
      <HeaderContent>
        {breadcrumbs && <BreadcrumbsWrap>{breadcrumbs}</BreadcrumbsWrap>}
        <AccentBar aria-hidden />
        <TitleRow direction="row" justify="space-between" align="start" wrap="wrap">
          <TextBlock>
            <Title>{title}</Title>
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
            {meta}
          </TextBlock>
          {actions && <ActionsRow>{actions}</ActionsRow>}
        </TitleRow>
        {children}
      </HeaderContent>
      <MonumentCaption nameEn={watermark.nameEn} nameGr={watermark.nameGr} />
    </HeaderContainer>
  );
};

export default PageHeader;
